import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  LuMessageSquare,
  LuMail,
  LuSearch,
  LuFilter,
  LuPaperclip,
  LuClock,
  LuTrash2,
  LuRefreshCw,
  LuChevronLeft,
  LuChevronRight,
  LuHardDrive,
  LuTimer,
  LuCircleCheck,
} from "react-icons/lu";
import type { ComponentType } from "react";
import { api, ApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonTable } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

const SINCE_OPTIONS = [
  { value: 1, label: "Last hour" },
  { value: 6, label: "Last 6 hours" },
  { value: 24, label: "Last 24 hours" },
  { value: 24 * 7, label: "Last 7 days" },
  { value: 24 * 30, label: "Last 30 days" },
];

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
}

export function MessagesPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [sinceHours, setSinceHours] = useState<number | "">("");
  const [hasAttachments, setHasAttachments] = useState<"" | "1" | "0">("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeDays, setPurgeDays] = useState(30);

  const messagesQuery = useQuery({
    queryKey: ["console", "admin", "messages", { page, q, sinceHours, hasAttachments }],
    queryFn: () =>
      api.adminMessages.list({
        page,
        limit: 50,
        q: q || undefined,
        sinceHours: sinceHours || undefined,
        hasAttachments: hasAttachments || undefined,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: api.adminMessages.bulkDelete,
    onSuccess: (res) => {
      toast.success(`Deleted ${res.deleted.toLocaleString()} message${res.deleted === 1 ? "" : "s"}.`);
      setSelected(new Set());
      setConfirmOpen(false);
      setPurgeOpen(false);
      qc.invalidateQueries({ queryKey: ["console", "admin", "messages"] });
      qc.invalidateQueries({ queryKey: ["console", "admin", "metrics"] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "Bulk delete failed");
    },
  });

  const data = messagesQuery.data;
  const messages = data?.messages ?? [];
  const allOnPageSelected =
    messages.length > 0 && messages.every((m) => selected.has(m.id));

  const selectedSummary = useMemo(() => {
    return Array.from(selected).slice(0, 3).join(", ");
  }, [selected]);

  function toggleAll() {
    if (allOnPageSelected) {
      const next = new Set(selected);
      for (const m of messages) next.delete(m.id);
      setSelected(next);
    } else {
      const next = new Set(selected);
      for (const m of messages) next.add(m.id);
      setSelected(next);
    }
  }

  function toggleOne(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  return (
    <>
      <PageHeader
        icon={LuMessageSquare}
        title="Messages"
        description="Inbound emails across every inbox. Inspect, filter, and bulk-delete."
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => messagesQuery.refetch()}
              disabled={messagesQuery.isFetching}
            >
              <LuRefreshCw className={`w-4 h-4 ${messagesQuery.isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              variant="danger"
              onClick={() => setPurgeOpen(true)}
              disabled={!data || data.stats.total === 0}
            >
              <LuTrash2 className="w-4 h-4" />
              Purge old
            </Button>
          </>
        }
      />

      <div className="p-8 space-y-6">
        {/* Stats */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Stat
              icon={LuMail}
              label="Total messages"
              value={data.stats.total.toLocaleString()}
            />
            <Stat
              icon={LuTimer}
              label="Last hour"
              value={data.stats.lastHour.toLocaleString()}
            />
            <Stat
              icon={LuClock}
              label="Last 24h"
              value={data.stats.last24h.toLocaleString()}
            />
            <Stat
              icon={LuPaperclip}
              label="With attachments"
              value={data.stats.withAttachments.toLocaleString()}
            />
            <Stat
              icon={LuHardDrive}
              label="Total size"
              value={formatBytes(data.stats.bytes)}
            />
          </div>
        )}

        {/* Filters */}
        <Card>
          <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center gap-2">
            <LuFilter className="w-4 h-4 text-[var(--color-text-subtle)]" />
            <p className="text-sm font-semibold text-[var(--color-text)]">Filters</p>
          </div>
          <div className="p-5 flex flex-wrap items-end gap-3">
            <div className="relative flex-1 min-w-[260px] max-w-md">
              <label className="block text-[11px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)] mb-2">
                Search
              </label>
              <LuSearch className="absolute left-3 top-1/2 mt-2.5 -translate-y-1/2 w-4 h-4 text-[var(--color-text-subtle)] pointer-events-none" />
              <input
                type="search"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
                placeholder="From, subject, body, or inbox address"
                className="w-full pl-10 pr-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)] focus:outline-none focus:border-[var(--color-accent)]"
              />
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)] mb-2">
                Time range
              </label>
              <select
                value={sinceHours}
                onChange={(e) => {
                  setSinceHours(e.target.value ? Number(e.target.value) : "");
                  setPage(1);
                }}
                className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent)]"
              >
                <option value="">All time</option>
                {SINCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)] mb-2">
                Attachments
              </label>
              <select
                value={hasAttachments}
                onChange={(e) => {
                  setHasAttachments(e.target.value as "" | "1" | "0");
                  setPage(1);
                }}
                className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent)]"
              >
                <option value="">Any</option>
                <option value="1">With attachments</option>
                <option value="0">Without</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="sticky top-0 z-10 bg-[var(--color-bg)] -mx-8 px-8 py-3 border-y border-[rgba(139,92,246,0.3)] flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm">
              <LuCircleCheck className="w-4 h-4 text-[var(--color-accent-light)]" />
              <span className="text-[var(--color-text)] font-medium">
                {selected.size.toLocaleString()} selected
              </span>
              <span className="text-[var(--color-text-subtle)] truncate max-w-md">
                {selected.size <= 3 ? selectedSummary : `${selectedSummary}, +${selected.size - 3} more`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                Clear
              </Button>
              <Button variant="danger" size="sm" onClick={() => setConfirmOpen(true)}>
                <LuTrash2 className="w-3.5 h-3.5" />
                Delete selected
              </Button>
            </div>
          </div>
        )}

        {/* Messages table */}
        {messagesQuery.isLoading && <SkeletonTable rows={8} cols={5} />}
        {!messagesQuery.isLoading && (
        <Card>

          {data && messages.length === 0 && (
            <div className="px-5 py-12 text-center">
              <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-[var(--color-elevated)] flex items-center justify-center">
                <LuMessageSquare className="w-5 h-5 text-[var(--color-text-subtle)]" />
              </div>
              <p className="text-sm text-[var(--color-text-muted)]">No messages match.</p>
            </div>
          )}

          {messages.length > 0 && (
            <>
              <table className="w-full text-sm">
                <thead className="bg-[var(--color-bg)]/40">
                  <tr className="text-left text-[var(--color-text-subtle)] border-b border-[var(--color-border)]">
                    <th className="px-5 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={toggleAll}
                        className="accent-[var(--color-accent)] cursor-pointer"
                      />
                    </th>
                    <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium">
                      From
                    </th>
                    <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium">
                      Subject
                    </th>
                    <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium">
                      Inbox
                    </th>
                    <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium text-right">
                      Size
                    </th>
                    <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium text-right">
                      Received
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((m) => (
                    <tr
                      key={m.id}
                      className={`border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-elevated)]/30 transition-colors ${
                        selected.has(m.id) ? "bg-[rgba(139,92,246,0.06)]" : ""
                      }`}
                    >
                      <td className="px-5 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(m.id)}
                          onChange={() => toggleOne(m.id)}
                          className="accent-[var(--color-accent)] cursor-pointer"
                        />
                      </td>
                      <td className="px-5 py-3 max-w-[200px]">
                        <p className="text-[var(--color-text)] truncate" title={m.fromAddress}>
                          {m.fromName || m.fromAddress}
                        </p>
                        {m.fromName && (
                          <p
                            className="text-[10px] text-[var(--color-text-subtle)] font-mono truncate"
                            title={m.fromAddress}
                          >
                            {m.fromAddress}
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-3 max-w-[300px]">
                        <div className="flex items-center gap-2">
                          {m.hasAttachments && (
                            <LuPaperclip className="w-3 h-3 text-[var(--color-text-subtle)] shrink-0" />
                          )}
                          <p
                            className="text-[var(--color-text)] truncate"
                            title={m.subject ?? "(no subject)"}
                          >
                            {m.subject || (
                              <em className="text-[var(--color-text-subtle)]">(no subject)</em>
                            )}
                          </p>
                        </div>
                        {m.bodyPreview && (
                          <p
                            className="text-[10px] text-[var(--color-text-subtle)] truncate mt-0.5"
                            title={m.bodyPreview}
                          >
                            {m.bodyPreview}
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-3 max-w-[200px]">
                        <p
                          className="text-xs font-mono text-[var(--color-text-muted)] truncate"
                          title={m.inboxAddress}
                        >
                          {m.inboxAddress}
                        </p>
                      </td>
                      <td className="px-5 py-3 text-right text-xs text-[var(--color-text-muted)] tabular-nums">
                        {formatBytes(m.rawSize)}
                      </td>
                      <td className="px-5 py-3 text-right text-xs text-[var(--color-text-subtle)] font-mono tabular-nums whitespace-nowrap">
                        {new Date(m.receivedAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {data && data.pagination.totalPages > 1 && (
                <div className="px-5 py-3 border-t border-[var(--color-border)] flex items-center justify-between text-xs">
                  <span className="text-[var(--color-text-muted)]">
                    Page {data.pagination.page} of {data.pagination.totalPages} ·{" "}
                    {data.pagination.total.toLocaleString()} total
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage(page - 1)}
                    >
                      <LuChevronLeft className="w-3.5 h-3.5" />
                      Prev
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={page >= data.pagination.totalPages}
                      onClick={() => setPage(page + 1)}
                    >
                      Next
                      <LuChevronRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
        )}
      </div>

      {/* Bulk-delete selected dialog */}
      <ConfirmDialog
        open={confirmOpen}
        title="Delete selected messages?"
        description={
          <div className="space-y-3">
            <p>
              Permanently remove{" "}
              <span className="text-[var(--color-text)] font-semibold">
                {selected.size.toLocaleString()}
              </span>{" "}
              message{selected.size === 1 ? "" : "s"} from the database.
            </p>
            <p className="text-xs text-[var(--color-text-subtle)]">
              This drops the message rows but does not affect attachments
              stored on R2 (run Data cleanup for that). Action can't be
              undone.
            </p>
          </div>
        }
        confirmLabel="Delete"
        destructive
        pending={deleteMutation.isPending}
        onClose={() => !deleteMutation.isPending && setConfirmOpen(false)}
        onConfirm={() => deleteMutation.mutate({ ids: Array.from(selected) })}
      />

      {/* Purge by age dialog */}
      <ConfirmDialog
        open={purgeOpen}
        title="Purge messages by age?"
        description={
          <div className="space-y-3">
            <p>
              Delete every message whose <code>received_at</code> is older
              than the threshold below.
            </p>
            <label className="block text-[11px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
              Older than (days)
            </label>
            <input
              type="number"
              min={0}
              max={3650}
              value={purgeDays}
              onChange={(e) =>
                setPurgeDays(Math.max(0, Math.min(3650, Number(e.target.value) || 0)))
              }
              className="w-32 px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent)] tabular-nums"
            />
            <p className="text-xs text-[var(--color-text-subtle)]">
              0 = delete everything. Action can't be undone.
            </p>
          </div>
        }
        confirmLabel={`Purge older than ${purgeDays}d`}
        destructive
        pending={deleteMutation.isPending}
        onClose={() => !deleteMutation.isPending && setPurgeOpen(false)}
        onConfirm={() => deleteMutation.mutate({ olderThanDays: purgeDays })}
      />
    </>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5 text-[var(--color-text-subtle)]" />
        <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
          {label}
        </p>
      </div>
      <p className="text-lg font-semibold text-[var(--color-text)] tabular-nums">{value}</p>
    </div>
  );
}
